/**
 * Shared hook for HTML5 drag-and-drop list reordering.
 * Provides drag state, event handlers, and visual feedback helpers.
 */

import { useState, useCallback, useRef } from 'react';

const INTERACTIVE_TAGS = new Set(['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA']);

/** Reorder an array by moving item at `from` to the insertion `slot`. */
export function reorder<T>(arr: T[], from: number, slot: number): T[] {
    const result = [...arr];
    const [moved] = result.splice(from, 1);
    const insertAt = slot > from ? slot - 1 : slot;
    result.splice(insertAt, 0, moved);
    return result;
}

export interface DragReorderResult {
    /** Index of the item currently being dragged, or null */
    dragIndex: number | null;
    /** Insertion slot index for the drop indicator, or null */
    dropSlot: number | null;

    /** Props to spread on each item's outer wrapper (handles dragOver) */
    getContainerProps: (index: number) => {
        onDragOver: (e: React.DragEvent) => void;
    };

    /** Props to spread on the draggable element inside each item */
    getDraggableProps: (index: number) => {
        draggable: true;
        onMouseDown: (e: React.MouseEvent) => void;
        onDragStart: (e: React.DragEvent) => void;
        onDragEnd: () => void;
    };
}

/**
 * Hook for drag-and-drop reordering of a list.
 * @param count - Number of items in the list
 * @param onReorder - Called with (fromIndex, toSlot) when a valid drop completes.
 *                    Use the exported `reorder()` helper to apply the move.
 * @param options.horizontal - Row layout (slot from the pointer's X against
 *                    the item's horizontal midpoint) instead of a column.
 */
export function useDragReorder(
    count: number,
    onReorder: (from: number, slot: number) => void,
    options?: { horizontal?: boolean },
): DragReorderResult {
    const horizontal = options?.horizontal ?? false;
    const canDragRef = useRef(false);
    const dragRef = useRef<number | null>(null);
    const dropRef = useRef<number | null>(null);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dropSlot, setDropSlot] = useState<number | null>(null);

    const handleDragStart = useCallback((index: number) => {
        dragRef.current = index;
        setDragIndex(index);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const from = dragRef.current;
        if (from === null) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const before = horizontal
            ? e.clientX < rect.left + rect.width / 2
            : e.clientY < rect.top + rect.height / 2;
        const slot = before ? index : index + 1;
        if (slot === from || slot === from + 1) {
            dropRef.current = null;
            setDropSlot(null);
        } else {
            dropRef.current = slot;
            setDropSlot(slot);
        }
    }, [horizontal]);

    const handleDragEnd = useCallback(() => {
        const from = dragRef.current;
        const slot = dropRef.current;
        if (from !== null && slot !== null) {
            onReorder(from, slot);
        }
        dragRef.current = null;
        dropRef.current = null;
        setDragIndex(null);
        setDropSlot(null);
    }, [onReorder]);

    const getContainerProps = useCallback((index: number) => ({
        onDragOver: (e: React.DragEvent) => handleDragOver(e, index),
    }), [handleDragOver]);

    const getDraggableProps = useCallback((index: number) => ({
        draggable: true as const,
        onMouseDown: (e: React.MouseEvent) => {
            let el = e.target as HTMLElement;
            while (el && el !== e.currentTarget) {
                if (INTERACTIVE_TAGS.has(el.tagName)) { canDragRef.current = false; return; }
                el = el.parentElement!;
            }
            canDragRef.current = true;
        },
        onDragStart: (e: React.DragEvent) => {
            if (!canDragRef.current) { e.preventDefault(); return; }
            e.dataTransfer.effectAllowed = 'move';
            handleDragStart(index);
        },
        onDragEnd: handleDragEnd,
    }), [handleDragStart, handleDragEnd]);

    return { dragIndex, dropSlot, getContainerProps, getDraggableProps };
}

/** Style for the blue insertion line indicator */
export const dropIndicatorStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#4a9eff',
    zIndex: 1,
    borderRadius: '1px',
};
